import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  BATCH_LANDED,
  MEMBERSHIP_SKIP,
  SEAT_REFUSAL,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { seatOptionLabel, type SeatContext, type SeatHolder } from './memberSeats';

const { report } = appStrings.modules.coachingDashboard.seatActions;

/** Client-minted, never a server word: these holders were never in the request. */
export const NO_ELIGIBLE_SEAT = 'no_eligible_seat';

export type ReportRow = Readonly<{
  key: string;
  /** Which person this row is about. Rows fold by it, so the headline counts students
   * while the rows stay seat-grain. */
  holderKey: string;
  name: string;
  /** Which seat the row is about, disambiguated where two rosters share an offering name.
   * Null where the row is about a person rather than one of their seats. */
  roster: string | null;
  /** This row failed and a SIBLING seat of the same person landed. The headline counts them
   * as not succeeded, which would otherwise hide the half that did. */
  hasOtherLanded: boolean;
  outcome: string;
  landed: boolean;
}>;

// Keyed off the contract's own words rather than restating them: a wire rename then
// breaks here instead of silently falling through to the unknown-word branch.
const OUTCOME_PROSE: Readonly<Record<string, string>> = {
  [BATCH_LANDED.add]: report.added,
  [BATCH_LANDED.remove]: report.removed,
  [BATCH_LANDED.move]: report.moved,
  [MEMBERSHIP_SKIP.seatNotOnCohortRoster]: report.seat_not_on_cohort_roster,
  [MEMBERSHIP_SKIP.membershipExists]: report.membership_exists,
  [MEMBERSHIP_SKIP.membershipNotFound]: report.membership_not_found,
  [MEMBERSHIP_SKIP.seatNotActive]: report.seat_not_active,
  [SEAT_REFUSAL.rosterCapacityExhausted]: report.roster_capacity_exhausted,
  [SEAT_REFUSAL.privilegedKindMove]: report.privileged_kind_move,
  [SEAT_REFUSAL.userAlreadyHasLiveSeat]: report.user_already_has_live_seat,
  [SEAT_REFUSAL.destinationIsOrigin]: report.destination_is_origin,
  [SEAT_REFUSAL.rosterSeatNotActive]: report.roster_seat_not_active,
  [SEAT_REFUSAL.rosterSeatNotFound]: report.roster_seat_not_found,
  [SEAT_REFUSAL.backrsCoachDirectAdd]: report.backrs_coach_direct_add,
  [SEAT_REFUSAL.coachRolesUnresolved]: report.coach_roles_unresolved,
  [SEAT_REFUSAL.rosterSeatNotLive]: report.roster_seat_not_live,
  [SEAT_REFUSAL.moveFailed]: report.move_failed,
  [SEAT_REFUSAL.notAttempted]: report.not_attempted,
  [SEAT_REFUSAL.pendingInviteExists]: report.pending_invite_exists,
  [NO_ELIGIBLE_SEAT]: report.noEligibleSeat,
};

/**
 * A word with no prose here is shown as it arrived. Reaching for the nearest familiar
 * one would state a reason the server did not give.
 */
export const outcomeProse = (outcome: string): string =>
  OUTCOME_PROSE[outcome] ?? report.unknown(outcome);

/** A word this client has no prose for, so its row states an outcome nobody expected. */
export const isUnknownOutcome = (outcome: string): boolean => OUTCOME_PROSE[outcome] === undefined;

type BatchItem = Readonly<{
  id: number;
  outcome: string;
  isOverCap?: boolean;
  revokedCohortMemberships?: number | null;
}>;

/**
 * The per-item answers plus the holders that never reached the request, in one list. The
 * landed word belongs to the door: only it knows whether 'added', 'removed' or 'moved'
 * is the one that means the item went through.
 */
export const buildReport = (
  items: readonly BatchItem[],
  contextBySeatId: ReadonlyMap<number, SeatContext>,
  landedWord: string,
  skipped: readonly SeatHolder[],
): ReportRow[] => {
  // Labelled against the seats this batch reports on, so two rosters sharing an offering
  // name are told apart by their managing institution — without it a person's two failed
  // seats render byte-identical lines.
  const seats = items
    .map((item) => contextBySeatId.get(item.id)?.seat)
    .filter((seat): seat is NonNullable<typeof seat> => seat !== undefined);

  const rows: ReportRow[] = [
    ...items.map((item, index) => {
      const context = contextBySeatId.get(item.id);

      return {
        // Keyed by position, not by seat: one batch may name the same seat twice, and the
        // second answer differs from the first.
        key: `item-${index}`,
        // A seat the selection cannot name is its own person rather than everyone's:
        // folding unnamed seats together would count them as one student.
        holderKey: context === undefined ? `seat-${item.id}` : `holder-${context.holder.id}`,
        name: context?.holder.name ?? String(item.id),
        roster: context === undefined ? null : seatOptionLabel(context.seat, seats),
        hasOtherLanded: false,
        outcome: item.outcome,
        landed: item.outcome === landedWord,
      };
    }),
    ...skipped.map((holder) => ({
      key: `holder-${holder.id}`,
      holderKey: `holder-${holder.id}`,
      name: holder.name,
      roster: null,
      hasOtherLanded: false,
      outcome: NO_ELIGIBLE_SEAT,
      landed: false,
    })),
  ];

  const landedHolders = new Set(rows.filter((row) => row.landed).map((row) => row.holderKey));

  return rows.map((row) =>
    row.landed || !landedHolders.has(row.holderKey) ? row : { ...row, hasOtherLanded: true },
  );
};

export const landedCount = (rows: readonly ReportRow[]): number =>
  rows.filter((row) => row.landed).length;

/**
 * The headline counts PEOPLE, not seats. Someone moved only if every seat they submitted
 * landed — a holder half of whose seats were refused did not move, and their per-seat rows
 * are what say which half. One student holding two seats would otherwise read as two.
 */
export const studentTally = (
  rows: readonly ReportRow[],
): Readonly<{ landed: number; total: number }> => {
  const byHolder = new Map<string, boolean>();

  rows.forEach((row) =>
    byHolder.set(row.holderKey, (byHolder.get(row.holderKey) ?? true) && row.landed),
  );

  return {
    landed: Array.from(byHolder.values()).filter(Boolean).length,
    total: byHolder.size,
  };
};

/**
 * What the whole batch pulled its people out of. The single move names this in its
 * confirm before acting; a batch cannot, so it is reported after — otherwise moving one
 * student says what it cost and moving fifty says nothing.
 */
export const cohortsRemoved = (items: readonly BatchItem[]): number | null => {
  const reported = items.filter(
    (item) => item.revokedCohortMemberships !== null && item.revokedCohortMemberships !== undefined,
  );

  // Null where NO item reported the field. Summing absences to zero would print "0
  // memberships removed from cohorts" — the stated consequence nobody counted, which is
  // the distinction the renderer's own contract turns on.
  if (reported.length === 0) return null;

  return reported.reduce((total, item) => total + (item.revokedCohortMemberships ?? 0), 0);
};

/**
 * Whether the batch pushed its destination past a soft cap. Read off the items that
 * LANDED: only a move that went through can have taken a roster over, and the flag rides
 * the same row. One answer for the batch — a warning is not per-person triage.
 */
export const landedOverCap = (items: readonly BatchItem[], landedWord: string): boolean =>
  items.some((item) => item.outcome === landedWord && item.isOverCap === true);
